import { StrictFetcher } from '@this/src/util';
/* eslint max-lines: 0 */
import React, { useEffect, useState } from 'react';
import { styled } from '@this/constants/themes';

import HotelList from '@this/domain/hotel/hotel_list';
import FlightList from '@this/domain/flight/flight_list';
import TransitList from '@this/domain/transit/transit_list';
import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import type SelectStore from '@this/src/domain/select_store';
import type SelectRepository from '@this/src/domain/select_repository';
import type SearchQueryItem from '@this/src/domain/search_query_item';
import type SearchResultItem from '@this/src/domain/search_result_item';

import type SearchQuery from '@this/domain/search_query';
import TransportListArea from './TransportListArea';
import HotelListArea from './HotelListArea';
import FlightListArea from './FlightListArea';

interface Props {
  query: SearchQuery;
  store: SelectStore;
  repository: SelectRepository;
  serviceId: number;
  icon: string;
  listWrapper: TransitList | HotelList | FlightList | null;
  type: string | undefined;
  subtype: number | undefined;
  show: boolean;
  hotelPriceLimit: number | null;
  queryItem?: SearchQueryItem;
  resultItem?: SearchResultItem;
  direction?: string;
  showSearchBox: boolean;
  toggleShowSearchBox: () => void;
  transportSegment: number | undefined;
  isOpenFlightCondition: boolean;
  errors: string[];
  handleTransportSegmentClick: (i: number) => () => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  setItemLoading: (loading: boolean) => void;
  handleFlightConditionSubmit: (
    filteredUpgradeSeat: boolean,
    carrierIds: string[],
    changeablePrices: string[],
    showConnectingAir: boolean
  ) => void;
  openFlightCondition: () => void;
  closeFlightCondition: () => void;
}
const ListAreaShortDistanceContainer: React.FC<Props> = props => {
  const {
    query,
    store,
    repository,
    serviceId,
    icon,
    listWrapper,
    type,
    subtype,
    show,
    hotelPriceLimit,
    queryItem,
    resultItem,
    direction,
    transportSegment,
    errors,
    showSearchBox,
    handleTransportSegmentClick,
    toggleShowSearchBox,
    handleSubmit,
    setItemLoading,
    handleFlightConditionSubmit,
    openFlightCondition,
    closeFlightCondition,
    isOpenFlightCondition
  } = props;
  const [shownFlightFilter, setShownFlightFilter] = useState(false);
  // TODO customHook化するなどして適切な箇所で呼びたい
  const [me, setMe] = useState<User | null>(null);

  useEffect(() => {
    StrictFetcher.get<UserJson>('/users.json', 'users/index.json').then(
      response => {
        setMe(new User(response));
      },
      () => {
        setMe(null);
      }
    );
  }, []);

  // ホテル専用のレンダリング関数
  const renderHotelListArea = () => {
    if (!(listWrapper instanceof HotelList)) {
      return null;
    }

    return (
      <HotelListArea
        query={query}
        store={store}
        repository={repository}
        serviceId={serviceId}
        icon={icon}
        listWrapper={listWrapper}
        hotelPriceLimit={hotelPriceLimit}
        queryItem={queryItem}
        resultItem={resultItem}
        showSearchBox={showSearchBox}
        toggleShowSearchBox={toggleShowSearchBox}
        errors={errors}
        handleSubmit={handleSubmit}
        me={me}
      />
    );
  };

  // 海外航空券専用のレンダリング関数
  const renderFlightListArea = () => {
    if (!(listWrapper instanceof FlightList) || subtype === undefined) {
      return null;
    }

    return (
      <FlightListArea
        store={store}
        repository={repository}
        icon={icon}
        listWrapper={listWrapper}
        subtype={subtype}
        queryItem={queryItem}
        resultItem={resultItem}
        shownFlightFilter={shownFlightFilter}
        setShownFlightFilter={setShownFlightFilter}
      />
    );
  };

  // 交通機関専用のレンダリング関数
  const renderTransportListArea = () => {
    if (!(listWrapper instanceof TransitList)) {
      return null;
    }

    return (
      <TransportListArea
        query={query}
        store={store}
        repository={repository}
        icon={icon}
        listWrapper={listWrapper}
        queryItem={queryItem}
        direction={direction}
        showSearchBox={showSearchBox}
        toggleShowSearchBox={toggleShowSearchBox}
        transportSegment={transportSegment}
        isOpenFlightCondition={isOpenFlightCondition}
        errors={errors}
        handleTransportSegmentClick={handleTransportSegmentClick}
        handleSubmit={handleSubmit}
        setItemLoading={setItemLoading}
        handleFlightConditionSubmit={handleFlightConditionSubmit}
        openFlightCondition={openFlightCondition}
        closeFlightCondition={closeFlightCondition}
      />
    );
  };

  // type別にレンダリング関数を呼び分け
  const renderContent = () => {
    // 共通のLoading処理
    if (resultItem?.loading) {
      return (
        <>
          <img className="select__loading" src="/images/loading.gif" width={50} height={50} alt="読み込み中" />
          {resultItem.selectedLoading === false && (
            <div className="select__loading-text">
              {type === 'hotel' ? 'ホテル' : type === 'transport' ? '経路' : '航空券'}を検索中です
            </div>
          )}
        </>
      );
    }

    switch (type) {
      case 'hotel':
        return renderHotelListArea();
      case 'transport':
        return renderTransportListArea();
      case 'flight':
        return renderFlightListArea();
      default:
        return null;
    }
  };

  return <StyledListArea className={`select-list-area ${show ? '' : 'hidden'}`}>{renderContent()}</StyledListArea>;
};

const StyledListArea = styled.div`
  flex-grow: 99999;
  background: ${props => (location.pathname.match(/trips\/\d+\/edit/) ? '#fff' : props.theme.accentColorLight)};
  padding: 10px;

  &.hidden {
    display: none;
  }
`;

export default ListAreaShortDistanceContainer;
